import {useCallback, useMemo, useRef, useState} from 'react';

import {equals, isNotNil, mergeAll} from 'ramda';

import {GetWarehousesResponse} from '@dms/api/warehouse';

import {QueryFilterObject} from 'features/eag-grid';

import {DataGridTreeFolderHandle} from '../types/treeFolder/DataGridTreeFolderHandle';

type TreeFolderFilters = {
  availableWarehouses?: GetWarehousesResponse;
  warehouseId?: string;
};

type TreeFolderState = {
  activeFolderId?: string;
  includeSubfolders: boolean;
};

export function useWarehouseDataGridTreeFolder(treeFolderFilters?: TreeFolderFilters) {
  const ref = useRef<DataGridTreeFolderHandle>(null);

  const [treeState, setTreeState] = useState<TreeFolderState>({
    activeFolderId: undefined,
    includeSubfolders: true,
  });

  const availableWarehousesIds = treeFolderFilters?.availableWarehouses?.map(
    (availableWarehouse) => availableWarehouse.id
  );

  const warehouseId = treeFolderFilters?.warehouseId;

  // Disabled eslint rule to fix issue with reseting static filters on reset filters button click
  // eslint-disable-next-line react/exhaustive-deps
  const getTreeFolderFilter = (activeFolderId?: string, includeSubfolders?: boolean) => {
    if (!activeFolderId) {
      return {treeFolderIds: undefined, treeFolderId: undefined};
    }

    if (includeSubfolders) {
      return {treeFolderIds: [activeFolderId], treeFolderId: undefined};
    }

    return {treeFolderIds: undefined, treeFolderId: activeFolderId};
  };

  // Memoize queryModifier to prevent unnecessary DataGrid re-renders
  const queryModifier = useCallback(
    (filter: QueryFilterObject) => {
      const treeFolderFilter = getTreeFolderFilter(
        treeState.activeFolderId,
        treeState.includeSubfolders
      );

      return mergeAll([
        filter,
        treeFolderFilter,
        {availableWarehousesList: availableWarehousesIds},
        {warehouseId: filter?.warehouseId ?? warehouseId},
      ]);
    },
    [
      getTreeFolderFilter, // eslint-disable-line react/exhaustive-deps -- Muted pre-existing violation
      treeState.activeFolderId,
      treeState.includeSubfolders,
      availableWarehousesIds,
      warehouseId,
    ]
  );

  // Memoize dataGridModifier to prevent unnecessary key changes and DataGrid re-renders.
  // Always return a valid modifier so the grid always receives queryModifier — even before
  // the ArticleFolderTree ref fires. Without this, the grid mounts without queryModifier on
  // the first render, loads stale data (URL params) containing static filter values, and
  // shows them as "Filtered by" chips because staticFilters is empty (T20-132568).
  const dataGridModifier = useMemo(() => {
    const key = `${treeState.activeFolderId ?? '0'}_${treeState.includeSubfolders}`;

    return {
      key,
      queryModifier,
    };
  }, [treeState.activeFolderId, treeState.includeSubfolders, queryModifier]);

  // eslint-disable-next-line typescript/no-explicit-any -- Pre-existing any
  const setRef = useCallback((node: any) => {
    if (isNotNil(node)) {
      ref.current = node;

      const newTreeState = {
        activeFolderId: node?.activeFolderId,
        includeSubfolders: node?.includeSubfolders ?? true,
      };

      // Only update state if there's an actual change to prevent unnecessary re-renders
      setTreeState((prevState) => {
        if (equals(prevState, newTreeState)) {
          return prevState;
        }
        return newTreeState;
      });
    }
  }, []);

  return [setRef, dataGridModifier, ref.current?.activeFolderId] as const;
}
